<template>
  <div class="app-container import-file-page">
    <!-- ========== 步骤一：上传文件 ========== -->
    <template v-if="step !== 'done'">
      <div class="upload-layout">
        <!-- 选择分区类型 & 重复数据处理 -->
        <section class="upload-panel partition-panel">
          <div class="partition-layout">
            <div class="partition-block">
              <div class="section-title">{{ $t('zoneImportFile.section.partitionType') }}</div>
              <div class="partition-type-row">
                <el-radio-group v-model="partitionType" class="partition-radio-group">
                  <el-radio label="1">{{ $t('zoneImportFile.partitionType.byPostCode') }}</el-radio>
                  <el-radio label="2">{{ $t('zoneImportFile.partitionType.byCity') }}</el-radio>
                  <el-radio label="3">{{ $t('zoneImportFile.partitionType.byCountry') }}</el-radio>
                </el-radio-group>
                <el-radio-group v-if="isPostCodePartition" v-model="postCodeMode" class="post-code-mode-group">
                  <el-radio label="normal">{{ $t('zoneImportFile.postCodeMode.normal') }}</el-radio>
                  <el-radio label="combination">{{ $t('zoneImportFile.postCodeMode.combination') }}</el-radio>
                </el-radio-group>
              </div>
            </div>
            <div class="partition-block">
              <div class="section-title">{{ $t('zoneImportFile.section.duplicate') }}</div>
              <el-radio-group v-model="importType" class="partition-radio-group">
                <!-- <el-radio label="0">{{ $t('zoneImportFile.importType.overwrite') }}</el-radio> -->
                <el-radio label="1">{{ $t('zoneImportFile.importType.clearAndImport') }}</el-radio>
              </el-radio-group>
            </div>
          </div>
        </section>

        <!-- 导入说明 -->
        <section class="upload-panel instruction-panel">
          <div class="instruction-main">
            <span class="info-badge">
              <i class="el-icon-info" />
            </span>
            <div class="instruction-content">
              <div class="section-title">{{ $t('zoneImportFile.section.instructions') }}</div>
              <div class="import-instructions">
                <p><i class="el-icon-success success-icon" /> {{ $t('zoneImportFile.instruction.downloadFirst') }}</p>
                <p><i class="el-icon-success success-icon" /> {{ $t('zoneImportFile.instruction.fileSupport') }}</p>
                <p><i class="el-icon-success success-icon" /> {{ $t('zoneImportFile.instruction.autoValidate') }}</p>
                <p class="warning-line"><i class="el-icon-warning warning-icon" /> {{ $t('zoneImportFile.instruction.duplicateWarning') }}</p>
              </div>
            </div>
          </div>
          <el-button class="download-template-btn" type="primary" plain size="small" icon="el-icon-download" @click="downloadTemplate">{{ $t('zoneImportFile.button.downloadTemplate') }}</el-button>
        </section>

        <!-- 上传文件 -->
        <section class="upload-panel file-panel">
          <div class="section-title">{{ $t('zoneImportFile.section.uploadFile') }}</div>
          <el-upload
            ref="elUpload"
            drag
            action=""
            :auto-upload="false"
            :on-change="handleFileChange"
            :on-exceed="handleExceed"
            :limit="1"
            accept=".xlsx,.xls"
            :show-file-list="false"
          >
            <div class="upload-drag-area">
              <i class="el-icon-upload upload-icon" />
              <div class="upload-text">{{ $t('zoneImportFile.upload.dragText') }}</div>
              <div class="upload-sub-text">{{ $t('zoneImportFile.upload.clickToChoose') }}</div>
              <el-button type="primary" size="small" class="btn-choose-file">{{ $t('zoneImportFile.button.chooseFile') }}</el-button>
            </div>
          </el-upload>

          <!-- 已上传文件（内嵌在上传区域内） -->
          <div v-if="uploadedFiles.length > 0" class="uploaded-files-wrap">
            <div v-for="(file, index) in uploadedFiles" :key="index" class="uploaded-file-item">
              <div class="file-left">
                <div class="file-icon-box">
                  <span class="file-ext">X</span>
                </div>
                <div class="file-info">
                  <div class="file-name">{{ file.name }}</div>
                  <div class="file-meta">
                    <span>{{ formatFileSize(file.size) }}</span>
                    <span class="file-meta-dot">•</span>
                    <span class="file-date">{{ file.date }}</span>
                  </div>
                </div>
              </div>
              <i class="el-icon-delete delete-btn" @click="removeFile(index)" />
            </div>
          </div>
        </section>
      </div>

      <!-- ========== 步骤二：校验结果 ========== -->
      <el-card v-loading="validating" class="result-card" shadow="never">
        <div class="result-title">{{ $t('zoneImportFile.section.validateResult') }}</div>

        <!-- 统计数据 -->
        <div class="result-stats">
          <span class="stat-item stat-total">
            <span class="stat-dot" />
            {{ $t('zoneImportFile.stats.total') }}<strong>{{ validateResult.total }}</strong>
          </span>
          <span class="stat-item stat-pass">
            <span class="stat-dot" />
            {{ $t('zoneImportFile.stats.passed') }}<strong>{{ validateResult.passed }}</strong>
          </span>
          <span class="stat-item stat-fail">
            <span class="stat-dot" />
            {{ $t('zoneImportFile.stats.failed') }}<strong>{{ validateResult.failed }}</strong>
          </span>
        </div>

        <!-- 分段进度条：通过（绿）+ 失败（红） -->
        <div class="result-progress-segmented">
          <div
            v-if="validateResult.passRate > 0"
            class="progress-segment progress-pass"
            :style="{ width: validateResult.passRate + '%' }"
          />
          <div
            v-if="validateResult.failed > 0"
            class="progress-segment progress-fail"
            :style="{ width: (validateResult.total > 0 ? Math.round((validateResult.failed / validateResult.total) * 100) : 0) + '%' }"
          />
        </div>

        <div class="result-bottom">
          <!-- 说明文字 -->
          <p class="result-note">{{ resultNote }}</p>

          <!-- 操作按钮 -->
          <el-button v-if="canDownloadErrorReport" plain size="small" icon="el-icon-download" @click="downloadErrorReport">{{ $t('zoneImportFile.button.downloadErrorReport') }}</el-button>
        </div>
      </el-card>

      <!-- 底部操作按钮 -->
      <div class="action-footer">
        <el-button @click="goBack">{{ $t('zoneImportFile.button.back') }}</el-button>
        <el-button type="primary" :loading="importing" :disabled="!canStartImport || validating" @click="startImport">
          {{ $t(importing ? 'zoneImportFile.button.importing' : 'zoneImportFile.button.startImport') }}
        </el-button>
      </div>
    </template>

    <!-- ========== 步骤三：导入完成 ========== -->
    <template v-if="step === 'done'">
      <div class="done-page">
        <el-card class="done-card" shadow="never">
          <div class="done-container">
            <span class="done-icon-wrap" :class="{ 'is-warning': !importResultFlag }">
              <i :class="[importResultFlag ? 'el-icon-check' : 'el-icon-warning-outline', 'done-icon']" />
            </span>
            <div class="done-title">{{ doneTitle }}</div>
            <div class="done-text">{{ doneText }}</div>
          </div>
        </el-card>

        <el-card class="import-stats-card" shadow="never">
          <div class="section-title">{{ $t('zoneImportFile.section.importStats') }}</div>
          <div class="import-stats-grid">
            <div class="import-stat-item import-stat-total">
              <div class="import-stat-label">{{ $t('zoneImportFile.stats.totalProcessed') }}</div>
              <div class="import-stat-value">{{ validateResult.total }}</div>
            </div>
            <div class="import-stat-item import-stat-success">
              <div class="import-stat-label">{{ $t('zoneImportFile.stats.importSuccess') }}</div>
              <div class="import-stat-value">{{ validateResult.passed }}</div>
            </div>
            <div class="import-stat-item import-stat-failed">
              <div class="import-stat-label">{{ $t('zoneImportFile.stats.importFailed') }}</div>
              <div class="import-stat-value">{{ validateResult.failed }}</div>
            </div>
          </div>
        </el-card>

        <el-card v-if="importResponseErrorRows.length > 0" class="error-detail-card" shadow="never">
          <div class="error-detail-header">
            <div class="section-title">{{ $t('zoneImportFile.section.errorDetails') }}</div>
            <el-button v-if="canDownloadErrorReport" plain size="small" icon="el-icon-download" @click="downloadErrorReport">{{ $t('zoneImportFile.button.exportErrorData') }}</el-button>
          </div>
          <el-table :data="importResponseErrorRows" border size="small" class="error-detail-table">
            <el-table-column prop="rowNumber" :label="$t('zoneImportFile.table.rowNumber')" width="72" />
            <el-table-column prop="partitionType" :label="$t('zoneImportFile.table.partitionType')" min-width="90" />
            <el-table-column prop="country" :label="$t('zoneImportFile.table.country')" min-width="90" />
            <el-table-column v-if="showImportStateColumn" prop="state" :label="$t('zoneImportFile.table.state')" min-width="110" />
            <el-table-column v-if="showImportCityColumn" prop="city" :label="$t('zoneImportFile.table.city')" min-width="110" />
            <el-table-column v-if="showImportPostCodeColumn" prop="startPostCode" :label="$t('zoneImportFile.table.startPostalCode')" min-width="120" />
            <el-table-column v-if="showImportPostCodeColumn" prop="endPostCode" :label="$t('zoneImportFile.table.endPostalCode')" min-width="120" />
            <el-table-column prop="zone" :label="$t('zoneImportFile.table.zone')" min-width="90" />
            <el-table-column prop="error" :label="$t('zoneImportFile.table.errorReason')" min-width="220">
              <template slot-scope="{ row }">
                <span class="error-reason">{{ formatImportError(row.error) }}</span>
              </template>
            </el-table-column>
          </el-table>
        </el-card>

        <div class="action-footer done-footer">
          <el-button @click="reUpload">{{ $t('zoneImportFile.button.reUpload') }}</el-button>
          <el-button type="primary" @click="goBack">{{ $t('zoneImportFile.button.backToZoneList') }}</el-button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import * as XLSX from 'xlsx';
import { logisticApi } from '@/api';
import { COUNTRY_NAME_AND_TWOCODE } from '@/utils/statics';

const patternStateName = /^(?=.*[A-Za-z\u4e00-\u9fa5])[A-Za-z\u4e00-\u9fa5\s]+$/;
const patternZone = /^[A-Za-z0-9](?:[A-Za-z0-9 ]*[A-Za-z0-9])?$/;
const patternPostCode = /^[A-Za-z0-9]+$/;
const patternCombinationPostCode = /^[0-9,-](?:[0-9, -]*[0-9,-])?$/;
const VALID_COUNTRY_CODES = new Set(COUNTRY_NAME_AND_TWOCODE.map(item => item.value));
const I18N_SCOPE = 'zoneImportFile';
const POST_CODE_MODE = {
  NORMAL: 'normal',
  COMBINATION: 'combination',
};
const PARTITION_TYPE_LABEL_KEYS = {
  1: 'partitionType.postCode',
  2: 'partitionType.city',
  3: 'partitionType.country',
};
const DUPLICATE_POSTAL_CODE_RANGE_ERROR = [
  'Import failed: Duplicate zone detail rows (identical data) found in the file. Please remove duplicate rows and import again',
  'Import failed: Duplicate postal code ranges found in the file. Please ensure each postal code range is unique and try again',
  'Alphanumeric postal codes do not support ranges. Start and end postal codes must be the same.',
  'Invalid postal code format',
];
const ZONE_VALIDATE_CONFIG = {
  1: ['Zone', 'Country', 'Start Postal Code', 'End Postal Code'],
  2: ['Zone', 'Country', 'State', 'City'],
  3: ['Zone', 'Country'],
};
const CITY_OPTIONAL_POST_CODE_COLUMNS = ['Start Postal Code', 'End Postal Code'];
const POST_CODE_COMBINATION_COLUMNS = ['Zone', 'Country', 'Postal Code'];

const ZONE_TEMPLATE_MAP = {
  1: {
    url: 'https://minio.wooper.com/test/11/ZoneTemplateByPostCode',
    fileName: 'ZoneTemplateByPostCode.xlsx',
  },
  2: {
    url: 'https://minio.wooper.com/test/11/ZoneTemplateByCity',
    fileName: 'ZoneTemplateByCity.xlsx',
  },
  3: {
    url: 'https://minio.wooper.com/test/11/ZoneTemplateByCountry',
    fileName: 'ZoneTemplateByCountry.xlsx',
  },
};
const POST_CODE_COMBINATION_TEMPLATE = {
  url: 'https://minio.wooper.com/test/11/ImportbyPostalCodeCombination',
  fileName: 'ZoneTemplateByPostCodeCombination.xlsx',
};

// 将接口或表格单元格值统一转为去首尾空格的字符串。
function normalizeImportCell(value) {
  if (value === undefined || value === null) return '';
  return String(value).trim();
}

// 兼容接口直接返回数据或包在 content 中的返回结构。
function getImportResponseContent(response) {
  if (!response) return {};
  if (response.content && typeof response.content === 'object' && !Array.isArray(response.content)) {
    return response.content;
  }
  return response;
}

// 根据导入页语言 key 获取当前语言文案。
function translateImportText(translate, key, values) {
  return translate(`${I18N_SCOPE}.${key}`, values);
}

// 根据导入接口返回值生成完成页统计数据和错误明细。
function buildImportDoneResult(response, total, translate) {
  const content = getImportResponseContent(response);
  const errorList = Array.isArray(content.errorList) ? content.errorList : [];
  const errorRows = errorList.map((item, index) => ({
    rowNumber: index + 1,
    country: normalizeImportCell(item.country),
    state: normalizeImportCell(item.state),
    city: normalizeImportCell(item.city),
    partitionType: PARTITION_TYPE_LABEL_KEYS[item.partitionType] ? translateImportText(translate, PARTITION_TYPE_LABEL_KEYS[item.partitionType]) : normalizeImportCell(item.partitionType),
    zone: normalizeImportCell(item.partitionValue),
    startPostCode: normalizeImportCell(item.postCodeFrom || item.startPostCode),
    endPostCode: normalizeImportCell(item.postCodeTo || item.endPostCode),
    error: normalizeImportCell(item.errorMsg || item.error),
  }));
  const importTotal = Math.max(Number(total) || 0, errorRows.length);
  const failed = errorRows.length;
  const passed = Math.max(importTotal - failed, 0);

  return {
    flag: content.flag !== false,
    validateResult: {
      total: importTotal,
      passed,
      failed,
      passRate: importTotal > 0 ? Math.round((passed / importTotal) * 100) : 0,
      hasLocalError: false,
    },
    errorRows,
  };
}

export default {
  name: 'BusinessLogisticsZoneImportFile',
  data() {
    return {
      step: 'upload', // upload | result | done
      partitionType: '1',
      postCodeMode: 'normal',
      importType: '1',
      uploadedFiles: [],
      validating: false,
      importing: false,
      validateResult: {
        total: 0,
        passed: 0,
        failed: 0,
        passRate: 0,
        hasLocalError: false,
      },
      hasValidateResult: false,
      validateRequestId: 0,
      localErrorRows: [],
      localSheetRows: [],
      importResponseErrorRows: [],
      importResultFlag: true,
      logisticsGroupId: null,
    };
  },
  computed: {
    isPostCodePartition() {
      return this.partitionType === '1';
    },
    isPostCodeCombinationMode() {
      return this.isPostCodePartition && this.postCodeMode === POST_CODE_MODE.COMBINATION;
    },
    currentTemplate() {
      if (this.isPostCodeCombinationMode) {
        return POST_CODE_COMBINATION_TEMPLATE;
      }
      return ZONE_TEMPLATE_MAP[this.partitionType] || ZONE_TEMPLATE_MAP[1];
    },
    // 判断当前是否存在可下载的本地或接口错误报告。
    canDownloadErrorReport() {
      return this.localErrorRows.length > 0 || this.importResponseErrorRows.length > 0;
    },
    // 判断当前校验结果是否允许发起导入。
    canStartImport() {
      return this.hasValidateResult && this.validateResult.total > 0 && this.validateResult.failed === 0;
    },
    // 判断错误明细表是否需要展示省/州列。
    showImportStateColumn() {
      return this.importResponseErrorRows.some(item => item.state);
    },
    // 判断错误明细表是否需要展示城市列。
    showImportCityColumn() {
      return this.importResponseErrorRows.some(item => item.city);
    },
    // 判断错误明细表是否需要展示邮编范围列。
    showImportPostCodeColumn() {
      return this.importResponseErrorRows.some(item => item.startPostCode || item.endPostCode);
    },
    // 根据接口导入结果生成完成页标题。
    doneTitle() {
      return this.tImport(this.importResultFlag ? 'done.title' : 'done.titleWithError');
    },
    // 根据接口导入结果生成完成页统计文案。
    doneText() {
      if (!this.importResultFlag || this.validateResult.failed > 0) {
        return this.tImport('done.textWithFailed', {
          total: this.validateResult.total,
          passed: this.validateResult.passed,
          failed: this.validateResult.failed,
        });
      }
      return this.tImport('done.textSuccess', { total: this.validateResult.total });
    },
    // 根据当前校验状态生成校验结果说明文案。
    resultNote() {
      if (this.validating) {
        return this.tImport('note.validating');
      }
      if (this.validateResult.hasLocalError) {
        return this.tImport('note.localError');
      }
      if (this.hasValidateResult && this.validateResult.failed === 0) {
        return this.tImport('note.passed');
      }
      return this.tImport('note.partial');
    },
  },
  watch: {
    // 切换分区类型后自动按新模板重新校验已上传文件。
    partitionType() {
      if (!this.isPostCodePartition) {
        this.postCodeMode = POST_CODE_MODE.NORMAL;
      }
      if (this.uploadedFiles.length > 0 && this.step !== 'done') {
        this.validateFile();
      }
    },
    postCodeMode() {
      if (this.uploadedFiles.length > 0 && this.step !== 'done') {
        this.validateFile();
      }
    },
  },
  // 获取logisticsGroupId
  created() {
    this.logisticsGroupId = this.$route.query.logisticsGroupId;
  },
  methods: {
    // 获取导入页当前语言文案。
    tImport(key, values) {
      return values ? this.$t(`${I18N_SCOPE}.${key}`, values) : this.$t(`${I18N_SCOPE}.${key}`);
    },

    // 将后端固定错误文案转换为当前语言展示文案。
    formatImportError(error) {
      const errorIndex = DUPLICATE_POSTAL_CODE_RANGE_ERROR.indexOf(error);
      if (errorIndex > -1) {
        return this.tImport(`error.duplicatePostalCodeRangeErrors.${errorIndex}`);
      }
      return error || '';
    },

    // 按当前分区类型下载对应的 Excel 导入模板。
    downloadTemplate() {
      const template = this.currentTemplate;
      const link = document.createElement('a');
      link.href = template.url;
      link.download = template.fileName;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    // 校验用户选择的 Excel 文件并触发本地解析校验。
    handleFileChange(file) {
      const isValid = /\.(xlsx|xls)$/i.test(file.name);
      if (!isValid) {
        this.$message.error(this.tImport('message.invalidFileType'));
        this.uploadedFiles = [];
        this.resetValidateState();
        this.$refs.elUpload && this.$refs.elUpload.clearFiles();
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        this.$message.error(this.tImport('message.fileTooLarge'));
        this.uploadedFiles = [];
        this.resetValidateState();
        this.$refs.elUpload && this.$refs.elUpload.clearFiles();
        return;
      }
      this.uploadedFiles = [{
        name: file.name,
        size: file.size,
        date: this.formatDate(new Date()),
        raw: file.raw,
      }];
      this.validateFile();
    },

    // 上传超过单文件限制时提示用户先移除已有文件。
    handleExceed() {
      this.$message.warning(this.tImport('message.oneFileOnly'));
    },

    // 从当前上传列表中移除文件并清空校验结果。
    removeFile(index) {
      this.uploadedFiles.splice(index, 1);
      this.resetValidateState();
      this.$refs.elUpload && this.$refs.elUpload.clearFiles();
    },

    // 重置本地校验、导入错误和导入状态。
    resetValidateState() {
      this.validateRequestId++;
      this.validating = false;
      this.hasValidateResult = false;
      this.localErrorRows = [];
      this.localSheetRows = [];
      this.importResponseErrorRows = [];
      this.importResultFlag = true;
      this.validateResult = this.getDefaultValidateResult();
    },

    // 返回校验结果的初始统计结构。
    getDefaultValidateResult() {
      return {
        total: 0,
        passed: 0,
        failed: 0,
        passRate: 0,
        hasLocalError: false,
      };
    },

    // 读取上传文件并按当前分区类型执行本地数据校验。
    async validateFile() {
      if (this.uploadedFiles.length === 0) {
        this.$message.warning(this.tImport('message.chooseFileFirst'));
        return;
      }
      const requestId = ++this.validateRequestId;
      this.validating = true;
      this.hasValidateResult = false;
      this.validateResult = this.getDefaultValidateResult();
      this.localErrorRows = [];
      this.localSheetRows = [];

      try {
        const rows = await this.readExcelRows(this.uploadedFiles[0].raw);
        if (requestId !== this.validateRequestId) return;
        this.localSheetRows = rows;
        this.validateResult = this.validateSheetRows(rows);
        this.hasValidateResult = true;
      } catch (e) {
        if (requestId !== this.validateRequestId) return;
        this.$message.error(this.tImport('message.parseFailed'));
      } finally {
        if (requestId === this.validateRequestId) {
          this.validating = false;
        }
      }
    },

    // 读取 Excel 首个工作表并转换为二维数组。
    readExcelRows(file) {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const data = new Uint8Array(reader.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          resolve(worksheet ? XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: '' }) : []);
        };
        reader.onerror = () => {
          reject(reader.error);
        };
        reader.readAsArrayBuffer(file);
      });
    },

    // 校验表头和有效数据行并汇总本地校验结果。
    validateSheetRows(rows) {
      const expectedColumns = this.getExpectedColumns();
      const headerRow = rows[0] || [];
      const dataRows = rows.slice(1)
        .map((row, index) => ({ row, rowNumber: index + 2 }))
        .filter(({ row }) => row.some(cell => this.normalizeCell(cell)));
      const headerErrors = this.validateHeader(headerRow, expectedColumns);
      const sheetColumns = this.getSheetColumns(headerRow, expectedColumns);
      const rowErrors = headerErrors.length > 0 ? [] : this.validateDataRows(dataRows, sheetColumns);
      const errorRows = [...headerErrors, ...rowErrors];
      const failedRowNumbers = new Set(rowErrors.map(row => row.rowNumber));
      const total = dataRows.length;
      const failed = headerErrors.length > 0 ? total : failedRowNumbers.size;
      const passed = Math.max(total - failed, 0);

      this.localErrorRows = errorRows;

      return {
        total,
        passed,
        failed,
        passRate: total > 0 ? Math.round((passed / total) * 100) : 0,
        hasLocalError: errorRows.length > 0,
      };
    },

    getExpectedColumns() {
      if (this.isPostCodeCombinationMode) {
        return POST_CODE_COMBINATION_COLUMNS;
      }
      return ZONE_VALIDATE_CONFIG[this.partitionType] || ZONE_VALIDATE_CONFIG[1];
    },

    getOptionalColumns() {
      return this.partitionType === '2' ? CITY_OPTIONAL_POST_CODE_COLUMNS : [];
    },

    getSheetColumns(headerRow, expectedColumns) {
      const actualColumns = headerRow.map(cell => this.normalizeCell(cell)).filter(Boolean);
      const optionalColumns = this.getOptionalColumns();
      const columnsWithOptional = [...expectedColumns, ...optionalColumns];
      const isWithOptional = optionalColumns.length > 0 &&
        actualColumns.length === columnsWithOptional.length &&
        columnsWithOptional.every((column, index) => this.normalizeHeader(actualColumns[index]) === this.normalizeHeader(column));

      return isWithOptional ? columnsWithOptional : expectedColumns;
    },

    // 校验 Excel 表头是否与当前分区类型模板一致。
    validateHeader(headerRow, expectedColumns) {
      const actualColumns = headerRow.map(cell => this.normalizeCell(cell)).filter(Boolean);
      const optionalColumns = this.getOptionalColumns();
      const columnsWithOptional = [...expectedColumns, ...optionalColumns];
      const expectedText = columnsWithOptional.join(this.tImport('report.separator'));
      const isSameColumns = actualColumns.length === expectedColumns.length &&
        expectedColumns.every((column, index) => this.normalizeHeader(actualColumns[index]) === this.normalizeHeader(column));
      const isSameOptionalColumns = optionalColumns.length > 0 &&
        actualColumns.length === columnsWithOptional.length &&
        columnsWithOptional.every((column, index) => this.normalizeHeader(actualColumns[index]) === this.normalizeHeader(column));

      if (isSameColumns || isSameOptionalColumns) return [];

      return [{
        rowNumber: 1,
        field: this.tImport('validation.headerField'),
        value: actualColumns.join(this.tImport('report.separator')),
        error: this.tImport('validation.headerRequiredColumns', { columns: expectedText }),
      }];
    },

    // 逐行逐列校验导入数据并收集错误信息。
    validateDataRows(dataRows, expectedColumns) {
      const errorRows = [];
      dataRows.forEach(({ row, rowNumber }) => {
        expectedColumns.forEach((column, columnIndex) => {
          const rawValue = row[columnIndex];
          const value = this.normalizeCell(rawValue);
          const error = this.validateCell(column, value, row, expectedColumns, rawValue);
          if (error) {
            errorRows.push({
              rowNumber,
              field: column,
              value,
              error,
            });
          }
        });
      });
      return errorRows;
    },

    // 校验单个导入单元格的必填、格式和邮编范围规则。
    validateCell(column, value, row, expectedColumns, rawValue) {
      const isPostCodeRangeColumn = column === 'Start Postal Code' || column === 'End Postal Code';
      if (column === 'State' && !value) return '';
      if (this.partitionType === '2' && isPostCodeRangeColumn && !value) return '';
      if (!value) return `${column}${this.tImport('validation.requiredSuffix')}`;
      const validateValue = column === 'Zone' || (this.isPostCodeCombinationMode && column === 'Postal Code') ? String(rawValue) : value;
      if (this.isPostCodeCombinationMode) {
        if (column === 'Zone' && !patternZone.test(validateValue)) return this.tImport('validation.zoneFormat');
        if (column === 'Country' && !VALID_COUNTRY_CODES.has(value)) return this.tImport('validation.countryFormat');
        if (column === 'Postal Code' && !this.isCombinationPostCodeValue(validateValue)) return this.tImport('validation.combinationPostalCodeFormat');
        return '';
      }
      if (column === 'Zone' && !patternZone.test(validateValue)) return this.tImport('validation.zoneFormat');
      if (column === 'Country' && !VALID_COUNTRY_CODES.has(value)) return this.tImport('validation.countryFormat');
      if (column === 'State' && !patternStateName.test(value)) return this.tImport('validation.stateFormat');
      if (isPostCodeRangeColumn && !patternPostCode.test(value)) {
        return `${column}${this.tImport('validation.lettersNumbersSuffix')}`;
      }
      if (column === 'End Postal Code') {
        const startIndex = expectedColumns.indexOf('Start Postal Code');
        const startValue = this.normalizeCell(row[startIndex]);
        if (patternPostCode.test(startValue) && patternPostCode.test(value) && startValue.localeCompare(value, 'en', { numeric: true, sensitivity: 'base' }) > 0) {
          return this.tImport('validation.postalCodeRange');
        }
      }
      return '';
    },

    isCombinationPostCodeValue(value) {
      if (!patternCombinationPostCode.test(value)) return false;
      if (String(value).split(',').some(item => !item.trim())) return false;
      const ranges = this.splitCombinationPostCode(value);
      return ranges.length > 0 && ranges.every(item => item.valid);
    },

    splitCombinationPostCode(value) {
      return String(value).split(',').map(item => item.trim()).filter(Boolean).map((item) => {
        const parts = item.split('-').map(part => part.trim());
        const valid = parts.length <= 2 && parts.every(part => /^\d+$/.test(part));
        const [start, end = start] = parts;
        return {
          valid,
          postCodeFrom: start,
          postCodeTo: end,
        };
      });
    },

    // 将单元格值规范化为去首尾空格的字符串。
    normalizeCell(value) {
      if (value === undefined || value === null) return '';
      return String(value).trim();
    },

    // 将表头值规范化为小写且合并空格的字符串。
    normalizeHeader(value) {
      return this.normalizeCell(value).replace(/\s+/g, ' ').toLowerCase();
    },

    // 根据当前页面状态下载本地校验错误或接口导入错误报告。
    downloadErrorReport() {
      if (this.step === 'done' && this.importResponseErrorRows.length > 0) {
        this.downloadImportErrorReport();
        return;
      }
      this.downloadLocalErrorReport();
    },

    // 下载接口返回的导入失败数据报告。
    downloadImportErrorReport() {
      const rows = [
        [
          this.tImport('table.rowNumber'),
          this.tImport('table.country'),
          this.tImport('table.partitionType'),
          this.tImport('table.zone'),
          this.tImport('table.startPostalCode'),
          this.tImport('table.endPostalCode'),
          this.tImport('table.state'),
          this.tImport('table.city'),
          this.tImport('table.errorReason'),
        ],
        ...this.importResponseErrorRows.map(item => [
          item.rowNumber,
          item.country,
          item.partitionType,
          item.zone,
          item.startPostCode,
          item.endPostCode,
          item.state,
          item.city,
          this.formatImportError(item.error),
        ]),
      ];
      const worksheet = XLSX.utils.aoa_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'ImportError');
      XLSX.writeFile(workbook, `${this.tImport('report.importErrorFile')}${this.formatDate(new Date()).replace(/[:\s-]/g, '')}.xlsx`);
    },

    // 下载本地校验阶段生成的错误报告。
    downloadLocalErrorReport() {
      const rows = this.buildErrorReportRows();
      const worksheet = XLSX.utils.aoa_to_sheet(rows);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Error');
      XLSX.writeFile(workbook, `${this.tImport('report.localErrorFile')}${this.formatDate(new Date()).replace(/[:\s-]/g, '')}.xlsx`);
    },

    // 将原始表格数据和本地错误信息组装为错误报告行。
    buildErrorReportRows() {
      const sourceRows = this.localSheetRows.length ? this.localSheetRows : [[]];
      const errorMap = this.localErrorRows.reduce((map, item) => {
        const message = item.field ? `${item.field}${this.tImport('report.fieldSeparator')}${item.error}` : item.error;
        if (!map[item.rowNumber]) map[item.rowNumber] = [];
        map[item.rowNumber].push(message);
        return map;
      }, {});
      const headerError = errorMap[1] ? errorMap[1].join(this.tImport('report.messageSeparator')) : '';
      const maxColumnLength = Math.max(...sourceRows.map(row => row.length), 0);
      const reportRows = sourceRows.map((row, index) => {
        const rowNumber = index + 1;
        const outputRow = [...row];
        while (outputRow.length < maxColumnLength) outputRow.push('');
        if (rowNumber === 1) {
          outputRow.push(this.tImport('report.validationStatus'));
        } else if (errorMap[rowNumber]) {
          outputRow.push(errorMap[rowNumber].join(this.tImport('report.messageSeparator')));
        } else {
          outputRow.push(headerError || this.tImport('report.validationPassed'));
        }
        return outputRow;
      });

      if (reportRows.length === 1 && headerError) {
        reportRows.push([...new Array(maxColumnLength).fill(''), headerError]);
      }

      return reportRows;
    },

    // 本地校验通过后组装请求并调用导入接口。
    startImport() {
      if (!this.hasValidateResult) {
        this.$message.warning(this.tImport('message.validateFirst'));
        return;
      }
      if (this.validateResult.hasLocalError) {
        this.$message.warning(this.tImport('message.fixLocalError'));
        return;
      }
      if (!this.localSheetRows.length) {
        this.$message.warning(this.tImport('message.emptyFile'));
        return;
      }
      this.importing = true;
      this.importResponseErrorRows = [];
      this.importResultFlag = true;
      const payload = this.buildImportPayload();
      logisticApi.getLogisticGroupDetailList(payload).then(res => {
        const content = getImportResponseContent(res);
        if (!res.failed || Array.isArray(content.errorList)) {
          const doneResult = buildImportDoneResult(res, payload.customerLogisticsGroupDetails.length, this.$t.bind(this));
          this.validateResult = {
            ...this.validateResult,
            ...doneResult.validateResult,
          };
          this.importResponseErrorRows = doneResult.errorRows;
          this.importResultFlag = doneResult.flag;
          this.step = 'done';
        }
      }).finally(() => {
        this.importing = false;
      });
    },

    // 从通过校验的表格行中构造导入接口请求体。
    buildImportPayload() {
      const errorRowNumbers = new Set(this.localErrorRows.map(r => r.rowNumber));
      const dataRows = this.localSheetRows.slice(1)
        .map((row, index) => ({ row, rowNumber: index + 2 }))
        .filter(({ row, rowNumber }) =>
          row.some(cell => this.normalizeCell(cell)) && !errorRowNumbers.has(rowNumber));
      const expectedColumns = this.getExpectedColumns();
      const sheetColumns = this.getSheetColumns(this.localSheetRows[0] || [], expectedColumns);
      const colIndex = {};
      sheetColumns.forEach((col, i) => { colIndex[col] = i; });

      const details = dataRows.reduce((list, { row }) => {
        const item = { logisticsGroupId: this.logisticsGroupId };
        if (colIndex['Country'] !== undefined) item.country = this.normalizeCell(row[colIndex['Country']]);
        if (this.isPostCodeCombinationMode) {
          list.push({
            ...item,
            postCode: this.normalizeCell(row[colIndex['Postal Code']]),
            partitionType: this.partitionType,
            partitionValue: this.normalizeCell(row[colIndex['Zone']]),
          });
          return list;
        }
        if (this.partitionType === '1') {
          item.postCodeFrom = this.normalizeCell(row[colIndex['Start Postal Code']]);
          item.postCodeTo = this.normalizeCell(row[colIndex['End Postal Code']]);
        }
        if (this.partitionType === '2') {
          const state = this.normalizeCell(row[colIndex['State']]);
          const postCodeFrom = this.normalizeCell(row[colIndex['Start Postal Code']]);
          const postCodeTo = this.normalizeCell(row[colIndex['End Postal Code']]);
          if (state) item.state = state;
          if (postCodeFrom) item.postCodeFrom = postCodeFrom;
          if (postCodeTo) item.postCodeTo = postCodeTo;
          item.city = this.normalizeCell(row[colIndex['City']]);
        }
        item.partitionType = this.partitionType;
        item.partitionValue = this.normalizeCell(row[colIndex['Zone']]);
        const remarkIndex = row.length > sheetColumns.length ? sheetColumns.length : -1;
        if (remarkIndex !== -1) {
          const remark = this.normalizeCell(row[remarkIndex]);
          if (remark) item.remark = remark;
        }
        list.push(item);
        return list;
      }, []);

      return {
        importType: this.importType,
        customerLogisticsGroupDetails: details,
      };
    },

    // 回到上传步骤并清空当前文件、导入方式和校验状态。
    reUpload() {
      this.step = 'upload';
      this.uploadedFiles = [];
      this.importType = '1';
      this.resetValidateState();
      this.$nextTick(() => {
        this.$refs.elUpload && this.$refs.elUpload.clearFiles();
      });
    },

    // 返回区域列表的上一层路由。
    goBack() {
      this.$router.back();
    },

    // 将文件字节数格式化为带单位的展示文本。
    formatFileSize(size) {
      if (!size) return '0 B';
      const units = ['B', 'KB', 'MB', 'GB'];
      let i = 0;
      while (size >= 1024 && i < units.length - 1) {
        size /= 1024;
        i++;
      }
      return `${size.toFixed(1)} ${units[i]}`;
    },

    // 将日期对象格式化为 yyyy-MM-dd HH:mm:ss。
    formatDate(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, '0');
      const d = String(date.getDate()).padStart(2, '0');
      const h = String(date.getHours()).padStart(2, '0');
      const min = String(date.getMinutes()).padStart(2, '0');
      const s = String(date.getSeconds()).padStart(2, '0');
      return `${y}-${m}-${d} ${h}:${min}:${s}`;
    },
  },
};
</script>

<style lang="scss" scoped>
.import-file-page {
  min-height: calc(100vh - 84px);
  background: #F5F5F5;

  .upload-layout {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  .upload-panel {
    background: #FFFFFF;
    border-radius: 6px;
    box-shadow: none;
    padding: 18px 22px;
  }

  .section-title {
    font-size: 13px;
    line-height: 18px;
    font-weight: 600;
    color: #1F2937;
    margin-bottom: 13px;
  }

  .partition-panel {
    padding-bottom: 20px;
  }

  .partition-layout {
    display: flex;
    gap: 48px;
  }

  .partition-block {
    flex: 1;
    min-width: 0;
  }

  .partition-type-row {
    display: flex;
    align-items: flex-start;
    gap: 20px;
  }

  .partition-radio-group {
    display: flex;
    flex-direction: column;
    gap: 11px;

    ::v-deep .el-radio {
      line-height: 18px;
      margin-right: 0;
    }

    ::v-deep .el-radio__label {
      font-size: 13px;
      color: #5F6B7A;
      padding-left: 7px;
    }
  }

  .post-code-mode-group {
    display: flex;
    align-items: center;
    min-height: 18px;

    ::v-deep .el-radio {
      line-height: 18px;
      margin-right: 18px;
    }

    ::v-deep .el-radio__label {
      font-size: 13px;
      color: #5F6B7A;
      padding-left: 7px;
    }
  }

  .instruction-panel {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 20px;
    padding-top: 24px;
    padding-bottom: 22px;
  }

  .instruction-main {
    display: flex;
    align-items: flex-start;
    min-width: 0;
  }

  .info-badge {
    width: 28px;
    height: 28px;
    margin-right: 18px;
    border-radius: 50%;
    background: #EDF4FF;
    color: #2F6BFF;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;

    i {
      font-size: 15px;
    }
  }

  .instruction-content {
    min-width: 0;
    padding-top: 1px;
  }

  .download-template-btn {
    flex-shrink: 0;
    height: 32px;
    padding: 8px 14px;
    border-color: #7AA7FF;
    color: #2F6BFF;
    background: #FFFFFF;
    border-radius: 4px;

    &:hover,
    &:focus {
      border-color: #2F6BFF;
      color: #2F6BFF;
      background: #F5F9FF;
    }
  }

  .import-instructions {
    p {
      margin: 6px 0;
      font-size: 12px;
      color: #556274;
      line-height: 18px;
    }
    .success-icon {
      color: #2DB56F;
      margin-right: 7px;
    }
    .warning-icon {
      color: #F27A1A;
      margin-right: 7px;
    }
    .warning-line {
      color: #F27A1A;
    }
  }

  .file-panel {
    padding-bottom: 18px;
  }

  .upload-drag-area {
    width: 100%;
    padding: 0;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;

    .upload-icon {
      font-size: 46px;
      color: #9AA3AF;
      margin-bottom: 18px;
    }
    .upload-text {
      font-size: 13px;
      line-height: 18px;
      color: #1F2937;
      font-weight: 500;
    }
    .upload-sub-text {
      margin-top: 9px;
      font-size: 12px;
      line-height: 17px;
      color: #6B7280;
    }
    .btn-choose-file {
      min-width: 86px;
      height: 32px;
      margin-top: 15px;
      padding: 8px 18px;
      background-color: #2F6BFF;
      border-color: #2F6BFF;
      border-radius: 4px;
      &:hover,
      &:focus {
        background-color: #2459D6;
        border-color: #2459D6;
      }
    }
  }

  // 覆盖 el-upload-dragger 默认样式
  ::v-deep .el-upload {
    display: block;
    width: 100%;
  }

  ::v-deep .el-upload-dragger {
    width: 100%;
    height: 260px;
    background: #FFFFFF;
    border: 1px dashed #C8D0DC;
    border-radius: 6px;
    display: flex;
    align-items: center;
    justify-content: center;
    &:hover {
      border-color: #2F6BFF;
      background: #F8FBFF;
    }
  }

  .uploaded-files-wrap {
    margin-top: 12px;
  }

  .uploaded-file-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    min-height: 52px;
    padding: 9px 14px;
    background: #FFFFFF;
    border: 1px solid #EAEEF3;
    border-radius: 5px;
    margin-bottom: 0;
    transition: box-shadow 0.2s;

    &:hover {
      box-shadow: 0 2px 8px rgba(15, 23, 42, 0.06);
    }

    .file-left {
      display: flex;
      align-items: center;
      gap: 10px;
      min-width: 0;
    }

    .file-icon-box {
      width: 28px;
      height: 28px;
      background: #E7F8EF;
      border-radius: 3px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .file-ext {
      font-size: 12px;
      line-height: 1;
      color: #16A35A;
      font-weight: 700;
    }

    .file-info {
      min-width: 0;

      .file-name {
        max-width: 520px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        font-size: 12px;
        line-height: 17px;
        color: #2B3440;
        font-weight: 500;
        margin-bottom: 3px;
      }
      .file-meta {
        font-size: 11px;
        line-height: 15px;
        color: #7C8796;
        .file-meta-dot {
          margin: 0 6px;
        }
      }
    }

    .delete-btn {
      font-size: 14px;
      color: #98A2B3;
      cursor: pointer;
      padding: 4px;
      border-radius: 4px;
      transition: color 0.2s, background 0.2s;
      &:hover {
        color: #F56C6C;
        background: #FEF2F2;
      }
    }
  }

  .action-footer {
    display: flex;
    justify-content: flex-end;
    gap: 12px;
    padding: 16px 0 4px;
  }

  @media (max-width: 768px) {
    .partition-layout {
      flex-direction: column;
      gap: 16px;
    }

    .instruction-panel {
      flex-direction: column;
    }

    .download-template-btn {
      margin-left: 46px;
    }

    ::v-deep .el-upload-dragger {
      height: 220px;
    }

    .uploaded-file-item .file-info .file-name {
      max-width: 220px;
    }

    .result-stats {
      grid-template-columns: 1fr;
      gap: 8px;

      .stat-pass,
      .stat-fail {
        justify-self: start;
      }
    }

    .result-bottom {
      flex-direction: column;
      align-items: flex-start;
    }

    .done-card {
      max-width: none;
    }

    .import-stats-grid {
      grid-template-columns: 1fr;
    }

    .error-detail-header {
      flex-direction: column;
      align-items: flex-start;
    }
  }

  // 校验结果页
  .result-card {
    border: 0;
    border-radius: 6px;
    margin-top: 16px;

    ::v-deep .el-card__body {
      padding: 20px 22px 18px;
    }
  }

  .result-title {
    font-size: 13px;
    line-height: 18px;
    font-weight: 600;
    color: #1F2937;
    margin-bottom: 17px;
  }

  .result-stats {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    align-items: center;
    margin-bottom: 14px;

    .stat-item {
      display: flex;
      align-items: center;
      gap: 6px;
      font-size: 12px;
      line-height: 17px;

      .stat-dot {
        width: 8px;
        height: 8px;
        border-radius: 50%;
        display: inline-block;
      }
    }

    .stat-total {
      color: #606266;
      .stat-dot { background: #909399; }
    }
    .stat-pass {
      justify-self: center;
      color: #67C23A;
      .stat-dot { background: #67C23A; }
    }
    .stat-fail {
      justify-self: end;
      color: #F56C6C;
      .stat-dot { background: #F56C6C; }
    }
  }

  .result-progress-segmented {
    display: flex;
    height: 4px;
    border-radius: 2px;
    background-color: #EDF0F5;
    overflow: hidden;
    margin-bottom: 20px;

    .progress-segment {
      height: 100%;
      transition: width 0.4s ease;
    }

    .progress-pass {
      background-color: #67C23A;
    }

    .progress-fail {
      background-color: #F56C6C;
    }
  }

  .result-bottom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 16px;
    min-height: 32px;
  }

  .result-note {
    font-size: 12px;
    line-height: 17px;
    color: #7C8796;
    margin: 0;
  }

  // 导入完成页
  .done-page {
    padding-bottom: 0;
  }

  .done-card {
    width: 100%;
    max-width: 520px;
    margin: 0 auto 24px;
    border: 0;
    border-radius: 6px;

    ::v-deep .el-card__body {
      padding: 26px 24px 28px;
    }
  }

  .done-container {
    text-align: center;
    padding: 0;

    .done-icon-wrap {
      width: 62px;
      height: 62px;
      margin: 0 auto 14px;
      border-radius: 50%;
      background: #EAF8EF;
      color: #12A85B;
      display: flex;
      align-items: center;
      justify-content: center;

      &.is-warning {
        background: #FFF7E8;
        color: #F59E0B;
      }
    }

    .done-icon {
      font-size: 34px;
      font-weight: 700;
    }

    .done-title {
      font-size: 20px;
      line-height: 28px;
      font-weight: 700;
      color: #1F2937;
      margin-bottom: 8px;
    }

    .done-text {
      font-size: 13px;
      line-height: 18px;
      color: #7C8796;
    }
  }

  .import-stats-card,
  .error-detail-card {
    border: 0;
    border-radius: 6px;
    margin-bottom: 16px;

    ::v-deep .el-card__body {
      padding: 20px 18px;
    }
  }

  .import-stats-grid {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 16px;
  }

  .import-stat-item {
    min-height: 78px;
    border: 1px solid #E5EAF2;
    border-radius: 6px;
    background: #F8FAFC;
    padding: 13px 14px;

    .import-stat-label {
      font-size: 12px;
      line-height: 17px;
      color: #7C8796;
      margin-bottom: 8px;
    }

    .import-stat-value {
      font-size: 26px;
      line-height: 32px;
      font-weight: 700;
      color: #1F2937;
    }
  }

  .import-stat-success {
    border-color: #BDEFD2;
    background: #ECFAF2;

    .import-stat-label,
    .import-stat-value {
      color: #11A968;
    }
  }

  .import-stat-failed {
    border-color: #FFD0D0;
    background: #FFF1F1;

    .import-stat-label,
    .import-stat-value {
      color: #E92E35;
    }
  }

  .error-detail-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    margin-bottom: 16px;

    .section-title {
      margin-bottom: 0;
    }
  }

  .error-detail-table {
    ::v-deep .el-table__header th {
      background: #F8FAFC;
      color: #5F6B7A;
      font-weight: 600;
    }

    .error-reason {
      color: #F04438;
    }
  }

  .done-footer {
    padding: 0 0 4px;
  }
}
</style>
